<script lang="ts">
    import allowDrag from '@amp/web-app-components/src/actions/allow-drag';
    import Artwork from '@amp/web-app-components/src/components/Artwork/Artwork.svelte';
    import { createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    export let lockup;
    export let dragEnabled: boolean = true;
    export let isEditorsChoice: boolean = false;
    export let inFavorites: boolean = false;
    export let isContainer: boolean = false;
    export let isVideo: boolean = false;

    $: headline = lockup.headline;
    $: title = lockup.title;
    $: subtitle = lockup.subtitle;
    $: isExplicit = lockup.showExplicitBadge;
    $: artwork = lockup.artwork;
    $: artworkProfile = isVideo ? 'featured-episode-video' : 'episode-lockup';

    let contextMenuIsOpen: boolean = false;
</script>

<div
    class="latest-release"
    class:latest-release--video={isVideo}
    class:is-editors-choice={isEditorsChoice}
    data-testid="latest-release"
    use:allowDrag={{
        dragEnabled,
        dragData: { items: [lockup.contentDescriptor] },
        isContainer,
        badgeCount: lockup.trackCount,
    }}
>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
        class="artwork-wrapper"
        class:has-open-menu={contextMenuIsOpen}
        on:click={() => dispatch('artworkClick')}
    >
        <Artwork {artwork} profile={artworkProfile} forceFullWidth={false} />

        <div data-testid="control" class="controls">
            <div data-testid="play-button" class="play-button menu-button">
                {#if $$slots['play-button']}
                    <slot name="play-button" />
                {/if}
            </div>
            {#if $$slots['context-menu']}
                <div
                    class="context-button menu-button"
                    data-testid="context-button"
                >
                    <slot name="context-menu" />
                </div>
            {/if}
        </div>
    </div>

    <ul class="lockup-lines">
        {#if headline}
            <li class="latest-release__headline" data-testid="headline">
                {headline}
            </li>
        {/if}

        <li class="latest-release__container">
            <div
                class="latest-release__title"
                class:latest-release__title--explicit={isExplicit}
                class:latest-release__title--favorited={inFavorites}
                data-testid="title"
            >
                {#if $$slots['title']}
                    <slot name="title" />
                {:else}
                    {title}
                {/if}
            </div>
            {#if $$slots['favorited-badge']}
                <slot name="favorited-badge" />
            {/if}
            {#if $$slots['explicit-badge']}
                <slot name="explicit-badge" />
            {/if}
        </li>

        {#if subtitle}
            <li class="latest-release__subtitle" data-testid="subtitle">
                {subtitle}
            </li>
        {/if}
        <li class="latest-release__add">
            {#if $$slots['buttons']}
                <slot name="buttons" />
            {/if}
        </li>
    </ul>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/viewports' as *;
    @use 'amp/stylekit/core/functions/dynamic-grids' as *;
    @use 'amp/stylekit/core/mixins/hover-style' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use 'amp/stylekit/core/mixins/overflow-bleed' as *;
    @use 'amp/stylekit/core/box-shadows' as *;
    @use '@amp/web-shared-styles/app/core/viewports' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/scrim-opacity-controller' as *;
    @use '@amp/web-shared-styles/app/core/mixins/aspect-ratio' as *;

    ///
    /// Set up latest release 2-column span block
    ///
    /// @param {String} $grid
    ///   The letter of the Grid (from Dynamic Grid spec)
    ///
    /// @param {Percent} $artwork-ratio
    ///   The percentage (including gutter) of the overall space that the artwork occupies
    ///
    @mixin setup-latest-release-block($grid, $artwork-ratio: 1, $columns: 2) {
        width: calc(100% - var(--bodyGutter));

        @each $viewport-name in viewport-list(default) {
            @if $viewport-name != xsmall {
                @media (--#{$viewport-name}) {
                    width: #{column-spans-width(
                            $grid,
                            $viewport-name,
                            $columns,
                            2
                        )};
                }
            }
        }
    }

    .latest-release {
        --iconCircleFillBGOverride: transparent; // Transparent to allow play-button material to be visible
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding-bottom: $spacerC;

        &:not(.is-editors-choice) {
            margin-inline-start: var(--bodyGutter);

            @include setup-latest-release-block(D, 1);
        }

        &.latest-release--video {
            @media (--small) {
                // Video layout of 2 `C` grid columns and aspect-ratio of `16:9`
                @include setup-latest-release-block(C, 1.778); // 16 / 9
            }
        }
    }

    .is-editors-choice {
        padding-bottom: 0;

        .artwork-wrapper {
            $editors-choice-artwork-size-mobile: 120px;
            $editors-choice-artwork-size-desktop: 160px;

            :global(.artwork-component) {
                --artwork-override-height: #{$editors-choice-artwork-size-mobile};
                --artwork-override-width: #{$editors-choice-artwork-size-mobile};
            }

            @media (--sidebar-large-visible) {
                :global(.artwork-component) {
                    --artwork-override-height: #{$editors-choice-artwork-size-desktop};
                    --artwork-override-width: #{$editors-choice-artwork-size-desktop};
                }
            }
        }

        .lockup-lines {
            max-width: 540px;
        }

        .latest-release__headline {
            margin-bottom: 2px;
        }

        .latest-release__title {
            font: var(--title-3-short);
            margin-bottom: 2px;

            @media (--sidebar-large-visible) {
                font: var(--title-2-short);

                @include line-clamp(3);
            }
        }

        .latest-release__subtitle {
            font: var(--body-short);

            @include line-clamp(1);

            @media (--sidebar-large-visible) {
                font: var(--title-3-short);

                @include line-clamp(2);
            }
        }

        .latest-release__add {
            margin-top: 19px;

            @media (--sidebar-large-visible) {
                margin-top: 15px;
            }
        }

        :global(.button .icon) {
            margin-inline-end: 3px;
        }

        :global(.button .icon svg) {
            height: 10px;

            @include target-chrome {
                top: 0.5px;
            }
        }
    }

    .latest-release__container {
        display: flex;
        align-items: center;

        :global(.favorite + .explicit-wrapper) {
            margin-inline-start: 4px;
        }

        :global(.explicit-wrapper) {
            margin-top: 2px;
        }
    }

    .latest-release--explicit {
        --explicitBadgeWidth: 10px;
    }

    .latest-release--favorited {
        --favoriteBadgeWidth: 10px;
    }

    .lockup-lines {
        white-space: normal;
        margin-inline-start: 20px;
        margin-inline-end: 20px;
    }

    .artwork-wrapper {
        position: relative;
        box-shadow: $global-default-content-box-shadow;
        cursor: pointer;
        border-radius: var(
            --global-border-radius-medium,
            #{$global-border-radius-medium}
        );

        @include scrim-opacity-controller;

        &::after {
            @include content-container-hover-style;
            @include scrim-opacity;
        }

        :global(.artwork-component) {
            // Albums are responsive in smaller viewports
            $latest-release-height: 165px;
            --artwork-override-width: calc(50vw - 50px);
            --artwork-override-height: auto;

            @media (--grid-layout-xs-2) {
                --artwork-override-width: #{$latest-release-height};
            }

            @media (--small) {
                @include aspect-ratio($height: $latest-release-height);
            }

            // Videos scale down in smaller viewports
            .latest-release--video & {
                --artwork-override-width: calc(50vw - 10px);

                @media (--sidebar-visible) {
                    --artwork-override-width: calc(
                        (100vw - var(--web-navigation-width)) / 2
                    );
                }

                // Higher specificity for `--musicVideo`
                @media (--small) {
                    @include aspect-ratio($height: $latest-release-height);
                }
            }
        }
    }

    .controls {
        width: 100%;
        height: 100%;
        top: 0;
        position: absolute;
        border-radius: inherit;
        transition: var(--global-transition);
        z-index: calc(var(--z-default) + 1);

        @include scrim-opacity;

        // Prevent any interaction with controls on touchscreen devices
        @media (--target-touchscreen) {
            visibility: hidden;
        }
    }

    .menu-button {
        bottom: 10px;
        position: absolute;
        line-height: 0; // Reset to normalize container height
        z-index: var(--z-default);
    }

    .context-button {
        inset-inline-end: 10px;
    }

    .play-button {
        inset-inline-start: 10px;
    }

    .badges {
        display: inline-flex;
        gap: 4px;
        margin-inline-start: 4px;
    }

    .latest-release__headline {
        margin-bottom: 6px;
        color: var(--systemSecondary);
        font: var(--subhead-emphasized);
    }

    .latest-release__title {
        --badgeSize: calc(
            var(--explicitBadgeWidth, 0px) + var(--favoriteBadgeWidth, 0px)
        ); // Used for positioning explicit and favorite badges inside line clamp
        margin-inline-end: 4px;
        font: var(--title-3-tall);

        @include line-clamp(2);
    }

    .latest-release__title--explicit {
        --explicitBadgeWidth: 12px;
    }

    .latest-release__title--favorited {
        --favoriteBadgeWidth: 10px;
        --favoriteBadgeMarginInlineStart: 0; // Use parent container margin
    }

    .latest-release__subtitle {
        color: var(--systemSecondary);
        font: var(--title-3-tall);

        @include line-clamp(2);
    }

    .latest-release__add {
        margin-top: 9px;
    }
</style>
